import { useCallback, useEffect, useReducer } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  locationToBillToLocationDTOAddOpp,
  locationToServiceLocationDTOAddOpp,
  type ServiceLocation,
  useGetRelatedLocationsOnMount,
} from '@user/store/entityLocations'

import { reducer } from './reducer'
import { initialState } from './initialState'
import {
  applySelectionActionCreator as applySelection,
  loadRelatedActionCreator as loadRelated,
  selectPrimaryServiceLocationActionCreator as selectPrimary,
  selectServiceLocationActionCreator as selectServiceLocation,
  updateServiceLocationActionCreator as updateServiceLocation,
} from './actions'
import { useAppDispatch } from '@user/store/hooks'
import {
  selectAddOpportunitySlice,
  setAddOppBillTo,
  setAddOppLocations,
  setAddOppResetBool,
} from '@user/store/opportunity/addOpportunity/addOpportunitySlice'
import { useSelector } from 'react-redux'

export function useCreateOppActions(
  redirectToAddOpp: boolean = false,
  handleDuplicateManageClick?: () => void,
) {
  const navigate = useNavigate()
  const appDispatch = useAppDispatch()
  const [state, dispatch] = useReducer(reducer, initialState)
  const { shouldRedirect, appliedSelections } = state

  //#region load related on billto selection
  const { items: relatedLocations } = useGetRelatedLocationsOnMount(
    state.primaryServiceLocation?.billTo.id,
  )

  useEffect(() => {
    dispatch(loadRelated(relatedLocations))

    const result = relatedLocations.map((x) =>
      locationToServiceLocationDTOAddOpp(x),
    )

    appDispatch(setAddOppLocations(result))
  }, [appDispatch, relatedLocations])
  //#endregion

  // if only one service location, select it
  useEffect(() => {
    if (state.serviceLocations.length === 1) {
      dispatch(applySelection(state.serviceLocations[0], redirectToAddOpp))
    }
  }, [redirectToAddOpp, state.serviceLocations])

  //#region callbacks
  const onClickCreateOpp = useCallback(
    (location: ServiceLocation) => {
      dispatch(selectPrimary(location))
      appDispatch(
        setAddOppBillTo(locationToBillToLocationDTOAddOpp(location.billTo)),
      )
    },
    [appDispatch],
  )

  const onClickCancel = useCallback(() => {
    dispatch(selectPrimary(undefined))
  }, [])

  const onCheckServiceLocation = useCallback(
    (id: string) => {
      const location = state.serviceLocations.find((x) => x.id == id)

      if (location) dispatch(selectServiceLocation(location))
    },
    [state.serviceLocations],
  )

  const onClickApply = useCallback(
    () => dispatch(applySelection(undefined, redirectToAddOpp)),
    [redirectToAddOpp],
  )
  //#endregion

  useEffect(() => {
    if (shouldRedirect) {
      if (handleDuplicateManageClick) handleDuplicateManageClick()
      navigate(
        {
          pathname: '/opportunities/addOpportunity',
          search: 'source=duplicate',
        },
        { state: appliedSelections },
      )
    }
  }, [appliedSelections, handleDuplicateManageClick, navigate, shouldRedirect])

  const { location } = useSelector(selectAddOpportunitySlice)

  useEffect(() => {
    if (location) {
      dispatch(updateServiceLocation(location))
      appDispatch(setAddOppResetBool())
    }
  }, [appDispatch, location])

  return {
    state,
    dispatch,
    onClickApply,
    onClickCancel,
    onClickCreateOpp,
    onCheckServiceLocation,
  }
}
